
import { useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectService } from '@/services/supabase/projectService';
import { Project } from '@/services/supabase/types';

const PROJECTS_QUERY_KEY = ['projects'];

export const useProjects = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: projects = [], isLoading: loading } = useQuery({
    queryKey: PROJECTS_QUERY_KEY,
    queryFn: () => projectService.getProjects(),
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 min
  });

  const createProjectMutation = useMutation({
    mutationFn: (project: Omit<Project, 'id' | 'created_at' | 'updated_at'>) =>
      projectService.createProject(project),
    onSuccess: (newProject) => {
      if (newProject) {
        queryClient.setQueryData<Project[]>(PROJECTS_QUERY_KEY, (old = []) => [newProject, ...old]);
        toast({ title: 'Éxito', description: 'Proyecto creado correctamente.' });
      }
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudo crear el proyecto.', variant: 'destructive' });
    },
  });

  const updateProjectMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Project> }) =>
      projectService.updateProject(id, data),
    onSuccess: (updatedProject, { id }) => {
      if (updatedProject) {
        queryClient.setQueryData<Project[]>(PROJECTS_QUERY_KEY, (old = []) =>
          old.map(p => (p.id === id ? updatedProject : p))
        );
        toast({ title: 'Éxito', description: 'Proyecto actualizado correctamente.' });
      }
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudo actualizar el proyecto.', variant: 'destructive' });
    },
  });

  const deleteProjectMutation = useMutation({
    mutationFn: (id: string) => projectService.deleteProject(id),
    onSuccess: (success, id) => {
      if (success) {
        queryClient.setQueryData<Project[]>(PROJECTS_QUERY_KEY, (old = []) =>
          old.filter(p => p.id !== id)
        );
        toast({ title: 'Éxito', description: 'Proyecto eliminado correctamente.' });
      }
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudo eliminar el proyecto.', variant: 'destructive' });
    },
  });

  const createProject = async (project: Omit<Project, 'id' | 'created_at' | 'updated_at'>) => {
    const result = await createProjectMutation.mutateAsync(project);
    if (!result) throw new Error('No se pudo crear el proyecto');
    return result;
  };

  const updateProject = async (id: string, projectData: Partial<Project>) => {
    try {
      const result = await updateProjectMutation.mutateAsync({ id, data: projectData });
      return !!result;
    } catch {
      return false;
    }
  };

  const getProjectById = useCallback(async (id: string): Promise<Project | null> => {
    try {
      const localProject = projects.find(p => p.id === id);
      if (localProject) return localProject;
      return await projectService.getProjectById(id);
    } catch (error) {
      console.error('Error al obtener proyecto:', error);
      return null;
    }
  }, [projects]);

  const deleteProject = async (id: string) => {
    try {
      const result = await deleteProjectMutation.mutateAsync(id);
      return result;
    } catch {
      return false;
    }
  };

  const refreshProjects = async () => {
    await queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
  };

  return {
    projects,
    loading,
    createProject,
    updateProject,
    getProjectById,
    deleteProject,
    refreshProjects,
  };
};

/** Invalidar proyectos desde cualquier lugar (reemplaza window events) */
export const invalidateProjects = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
};

export { PROJECTS_QUERY_KEY };
